import { safeAbsurd } from '@ipfabric/utils';
import classNames from 'classnames';
import { renderInlineChanges } from 'components/Diff/helpers/renderInlineChanges';
import { renderInlineDiffChanges } from 'components/Diff/helpers/renderInlineDiffChanges';
import {
  type DiffDirection,
  renderSideBySideChanges,
} from 'components/Diff/helpers/renderSideBySideChanges';
import { Select } from 'components/Select/Select';
import { createSelectOptions } from 'components/Select/helpers/createSelectOptions';
import { type Change, diffLines } from 'diff';
import { memo, useMemo, useState } from 'react';
import type { FC, ReactNode } from 'react';
import './Diff.css';
import { sleep } from '@ipfabric/ui-components/utils/sleep';
import { LoaderAndText } from 'containers/LoaderAndText/LoaderAndText';

type Diffable = {
  text: string;
  timestamp?: number;
  title?: ReactNode;
  renderTitle?: () => ReactNode;
};

type Props = {
  className?: string;
  content: [Diffable, Diffable];
  title?: ReactNode;
};

const diffStyles = ['inline', 'sideBySide', 'inlineDiff'] as const;
type DiffStyle = (typeof diffStyles)[number];

type DiffHeaderProps = {
  style: DiffStyle;
  content: [Diffable, Diffable];
  direction: DiffDirection;
};

const DiffHeader = memo((props: DiffHeaderProps) => {
  const [left, right] = props.content;
  if (!left.renderTitle && !right.renderTitle) {
    return null;
  }

  return (
    <div className='flex flex-row bg-blue-50'>
      <div className='flex flex-1 gap-2 py-2 px-4 items-center diff-sidebyside-left'>
        {left.renderTitle?.()}
      </div>
      <div className='flex flex-1 gap-2 py-2 px-4 items-center'>
        {right.renderTitle?.()}
      </div>
    </div>
  );
});

type DiffContentProps = {
  diff: Change[];
  style: DiffStyle;
  direction: DiffDirection;
};

const DiffContent = memo((props: DiffContentProps) => {
  switch (props.style) {
    case 'inline':
      return renderInlineChanges(props.diff);
    case 'sideBySide':
      return renderSideBySideChanges(props.diff, props.direction);
    case 'inlineDiff': {
      const renderedChanges = renderInlineDiffChanges(props.diff);
      if (renderedChanges.length > 0) {
        return renderedChanges;
      }
      return <p className='text-gray-800 p-2'>There are no changes.</p>;
    }
    default:
      safeAbsurd(props.style, 'Unexpected diff style');
      return null;
  }
});

export const Diff: FC<Props> = (props) => {
  const { diff, direction } = useMemo(() => {
    const [first, second] = props.content;
    if ((first.timestamp ?? 0) < (second.timestamp ?? 0)) {
      return {
        diff: diffLines(first.text, second.text),
        direction: 'left-to-right',
      } as const;
    }

    return {
      diff: diffLines(second.text, first.text),
      direction: 'right-to-left',
    } as const;
  }, [props.content]);

  const [style, setStyle] = useState<DiffStyle>('sideBySide');
  const [rendering, setRendering] = useState(false);
  const styleOptions = useMemo(
    () =>
      createSelectOptions(diffStyles, {
        sideBySide: 'Side by side',
        inline: 'Inline',
        inlineDiff: 'Inline diff',
      }),
    [],
  );

  const setStyleWithRendering = async (s: DiffStyle) => {
    try {
      setRendering(true);
      // let the rendering indicator animation render
      await sleep(0);
      setStyle(s);
    } finally {
      setRendering(false);
    }
  };

  return (
    <>
      <div className='flex flex-row flex-wrap items-center gap-2'>
        {props.title}
        <div className='flex gap-2 items-center'>
          <strong>Style</strong>
          <Select
            className='w-28'
            nonClearable
            onChange={setStyleWithRendering}
            options={styleOptions}
            size='tiny'
            value={style}
          />
        </div>
      </div>
      {rendering && <LoaderAndText />}
      <div
        className={classNames(
          'border border-gray-600 rounded-sm',
          // properly render rounded borders
          'overflow-hidden',
          rendering && 'hidden',
        )}
      >
        <DiffHeader
          content={props.content}
          style={style}
          direction={direction}
        />
        <DiffContent diff={diff} style={style} direction={direction} />
      </div>
    </>
  );
};
